import {List, Set} from 'immutable';
import {isEmail} from 'validator';

import errorCodes from 'client/errorCodes';
import constants from 'client/constants';
import {isManagedAccount} from 'client/Classrooms/shared/enrollUtils';

import {arrayToReadableString} from 'lib/stringUtils';

// ---------- Validation creators -------------

const basicValidatorsMap = {
  nameRequired: [
    (item) => item.get('firstName')?.length && item.get('lastName')?.length,
    'names',
    'First and last names are required'
  ],
  nameCharValidation: [
    (item) => !item.get('firstName')?.includes('.') && !item.get('lastName')?.includes('.'),
    'names',
    'Periods are not allowed in first and last names'
  ],
  email: [(item) => isEmail(item.get('email')), 'email', 'Not a valid email address'],
  username: [
    (item) => constants.REGEX_COLLECTION.USERNAME.test(item.get('username')),
    'username',
    'Usernames must be 4-16 alphanumeric characters long'
  ],
  password: [
    (item) => constants.REGEX_COLLECTION.PASSWORD.test(item.get('password')),
    'password',
    'Passwords must have at least 8 characters and at least one number or special character'
  ]
};

const basicValidators = [
  basicValidatorsMap.nameRequired,
  basicValidatorsMap.nameCharValidation,
  basicValidatorsMap.email,
  basicValidatorsMap.username,
  basicValidatorsMap.password
];

export const createBasicValidatons = (emailDomains) => {
  const domainsArr = [...emailDomains.map((domain) => domain.getName())];
  const prettyEmails = arrayToReadableString(domainsArr, 'or');
  const validators = [
    ...basicValidators,
    [
      (item) => isManagedAccount(item.get('email'), emailDomains),
      'domain',
      `Emails must end with ${prettyEmails}`
    ]
  ];

  return validators;
};

export const createRecordValidator = (validators) => {
  return (records) => {
    return records.reduce(
      ([entries, validEntries, invalidEntries], curr) => {
        const [surfacedErrorCode, surfacedErrorMessage] = validators.reduce(
          (prevErrorPair, [isValid, errorCode, errorMessage]) => {
            if (prevErrorPair.length) {
              return prevErrorPair;
            }
            return isValid(curr) ? prevErrorPair : [errorCode, errorMessage];
          },
          []
        );

        return !surfacedErrorCode
          ? [entries, validEntries.push(curr), invalidEntries]
          : [
              entries,
              validEntries,
              invalidEntries.push(
                curr.set('error', surfacedErrorCode).set('errorMessage', surfacedErrorMessage)
              )
            ];
      },
      [records, List(), List()]
    );
  };
};

export const managedUserErrorMap = {
  DUPLICATE_EMAIL: errorCodes.BAD_REQUEST.VALIDATION_ERROR.DUPLICATE_EMAIL,
  DUPLICATE_USERNAME: errorCodes.BAD_REQUEST.VALIDATION_ERROR.DUPLICATE_USERNAME,
  UNIQUE_EMAIL_CONSTRAINT_VIOLATED:
    errorCodes.BAD_REQUEST.VALIDATION_ERROR.UNIQUE_EMAIL_CONSTRAINT_VIOLATED,
  UNIQUE_USERNAME_CONSTRAINT_VIOLATED:
    errorCodes.BAD_REQUEST.VALIDATION_ERROR.UNIQUE_USERNAME_CONSTRAINT_VIOLATED
};

const initialValidationState = {
  duplicateEmails: [],
  duplicateUsernames: [],
  existingEmails: [],
  existingUsernames: []
};

export const createBadRequestValidations = (errors) => {
  const {duplicateEmails, duplicateUsernames, existingEmails, existingUsernames} = errors.reduce(
    (acc, cur) => {
      const {code, meta} = cur;
      if (code === managedUserErrorMap.DUPLICATE_EMAIL) {
        const {email} = meta;
        acc.duplicateEmails = [...acc.duplicateEmails, email];
      }
      if (code === managedUserErrorMap.DUPLICATE_USERNAME) {
        const {username} = meta;
        acc.duplicateUsernames = [...acc.duplicateUsernames, username];
      }
      if (code === managedUserErrorMap.UNIQUE_EMAIL_CONSTRAINT_VIOLATED) {
        const {email} = meta;
        acc.existingEmails = [...acc.existingEmails, email];
      }
      if (code === managedUserErrorMap.UNIQUE_USERNAME_CONSTRAINT_VIOLATED) {
        const {username} = meta;
        acc.existingUsernames = [...acc.existingUsernames, username];
      }
      // check for error code matching
      return acc;
    },
    initialValidationState
  );

  const uniqueValidations = [];
  const duplicateValidations = [];
  if (existingEmails.length) {
    existingEmails.forEach((email) => {
      uniqueValidations.push([
        (item) => item.get('email') !== email,
        'nonUniqueEmail',
        'An account with this email address already exists'
      ]);
    });
  }
  if (existingUsernames.length) {
    existingUsernames.forEach((username) => {
      uniqueValidations.push([
        (item) => item.get('username') !== username,
        'nonUniqueUsername',
        'An account with this username already exists'
      ]);
    });
  }
  if (duplicateEmails.length) {
    duplicateEmails.forEach((email) => {
      duplicateValidations.push([
        (item) => item.get('email') !== email,
        'duplicateEmail',
        'This email address appears more than once. Only one instance of each duplicate will be used.'
      ]);
    });
  }
  if (duplicateUsernames.length) {
    duplicateUsernames.forEach((username) => {
      duplicateValidations.push([
        (item) => item.get('username') !== username,
        'duplicateUsername',
        'This username appears more than once. Only the first instance of each duplicate will be used.'
      ]);
    });
  }
  return [uniqueValidations, duplicateValidations];
};

// --------- Validation Handlers ------------

export const getOfflineValidations = (records, emailDomains) => {
  const validations = createBasicValidatons(emailDomains);
  const validator = createRecordValidator(validations);
  const [entries, validEntries, invalidEntries] = validator(records);
  return {entries, validEntries, invalidEntries};
};

export const getServerValidations = (records, emailDomains, error) => {
  const basicValidations = createBasicValidatons(emailDomains);
  let validations = [...basicValidations];
  let duplicateValidators = [];

  // if server error, handle bad request errors
  if (error) {
    const errors = error?.response?.body?.errors;
    const [backendErrorValidations, duplicateValidations] = createBadRequestValidations(errors);
    validations = [...validations, ...backendErrorValidations, ...duplicateValidations];
    duplicateValidators = duplicateValidations;
  }
  const validator = createRecordValidator(validations);
  const [entries, validEntries, invalidEntries] = validator(records);

  if (!invalidEntries.isEmpty()) {
    // Check for duplicates, get first instance of valid duplicate entry
    const [updatedEntries, updatedInvalid] = handleDuplicates(invalidEntries, duplicateValidators);
    const updatedValid = validEntries.merge(updatedEntries);
    return {
      entries,
      validEntries: updatedValid,
      invalidEntries: updatedInvalid
    };
  }
  return {entries, validEntries, invalidEntries};
};

export const handleDuplicates = (invalidEntries, duplicateValidations) => {
  let duplicateRecords = List();
  let updatedInvalidRecords = List();
  invalidEntries.forEach((entry) => {
    if (entry.get('error') === 'duplicateEmail' || entry.get('error') === 'duplicateUsername') {
      duplicateRecords = duplicateRecords.push(entry);
    } else {
      updatedInvalidRecords = updatedInvalidRecords.push(entry);
    }
  });
  // get all duplicate errors for each entry
  const entryErrors = duplicateRecords.reduce((errorEntryPairs, curr) => {
    const errors = duplicateValidations.reduce((errArray, [isValid, errorCode, errorMessage]) => {
      return isValid(curr) ? errArray : [...errArray, [errorCode, errorMessage]];
    }, []);

    return [...errorEntryPairs, [curr, errors]];
  }, []);
  // sort entries by number of validations
  const sortedErrors = entryErrors.sort((entryA, entryB) => entryB[1].length - entryA[1].length);
  // Get first unique instance of duplicate email/username
  const [newValidEntries] = sortedErrors.reduce(
    ([validEntries, {emails, usernames}], entry) => {
      const [record, _errors] = entry;
      if (!emails.has(record.get('email')) && !usernames.has(record.get('username'))) {
        return [
          validEntries.push(record),
          {
            emails: emails.add(record.get('email')),
            usernames: usernames.add(record.get('username'))
          }
        ];
      }
      updatedInvalidRecords = updatedInvalidRecords.push(record);
      return [validEntries, {emails, usernames}];
    },
    [List(), {emails: Set(), usernames: Set()}]
  );

  return [newValidEntries, updatedInvalidRecords];
};
